import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Edit, Check, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { db, storage } from '../firebase';
import { doc, getDoc, updateDoc, serverTimestamp, onSnapshot, deleteField } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

export default function OrderTracking() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { userRole } = useAuth();

  // State
  const [isEditingAmount, setIsEditingAmount] = useState(false);
  const [editedAmount, setEditedAmount] = useState('');
  const [updatingAmount, setUpdatingAmount] = useState(false);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);
  const [product, setProduct] = useState(null);
  const [paymentConfig, setPaymentConfig] = useState(null);

  // Template editing state
  const [isEditingTemplate, setIsEditingTemplate] = useState(false);
  const [selectedTemplateIndex, setSelectedTemplateIndex] = useState(null);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [customText, setCustomText] = useState('');
  const [uploadingImages, setUploadingImages] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Payment state
  const [uploadingPayment, setUploadingPayment] = useState(false);

  // Image lightbox state
  const [lightboxImage, setLightboxImage] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('onlineTransfer');

  // Set default payment method based on order or product settings
  useEffect(() => {
    if (order?.payment?.method) {
      setSelectedPaymentMethod(order.payment.method);
    } else if (product?.paymentOptions?.paymentMethods) {
      const methods = product.paymentOptions.paymentMethods;
      if (methods.onlineTransfer) setSelectedPaymentMethod('onlineTransfer');
      else if (methods.creditDebit) setSelectedPaymentMethod('creditDebit');
      else if (methods.kokoPay) setSelectedPaymentMethod('kokoPay');
      else if (methods.cod) setSelectedPaymentMethod('cod');
    }
  }, [product, order?.payment?.method]);

  // Fetch order data with real-time updates
  useEffect(() => {
    if (!orderId) {
      navigate('/');
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, 'orders', orderId),
      (docSnapshot) => {
        if (!docSnapshot.exists()) {
          navigate('/');
          return;
        }

        const orderData = { id: docSnapshot.id, ...docSnapshot.data() };
        const expiredStatuses = ['COMPLETED', 'DELIVERED', 'CANCELLED'];

        if (expiredStatuses.includes(orderData.status)) {
          setExpired(true);
        } else {
          setExpired(false);
        }

        setOrder(orderData);

        // Initialize template state if already selected
        if (orderData.template && !isEditingTemplate) {
          setCustomText(orderData.template.customerNote || '');
          setUploadedImages(orderData.template.images || []);
        }

        setLoading(false);
      },
      (error) => {
        console.error('Error fetching order:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [orderId, navigate, isEditingTemplate]);

  // Fetch product details
  useEffect(() => {
    const fetchProduct = async () => {
      const productIdentifier = order?.productId || order?.productSlug;
      if (!productIdentifier) {
        console.warn('No product identifier found in order');
        return;
      }

      try {
        const productDoc = await getDoc(doc(db, 'products', productIdentifier));
        if (productDoc.exists()) {
          setProduct({ id: productDoc.id, ...productDoc.data() });
        } else {
          console.warn('Product not found:', productIdentifier);
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      }
    };

    fetchProduct();
  }, [order]);

  // Fetch payment config
  useEffect(() => {
    const fetchPaymentConfig = async () => {
      try {
        const configDoc = await getDoc(doc(db, 'settings', 'paymentConfig'));
        if (configDoc.exists()) {
          setPaymentConfig(configDoc.data());
        }
      } catch (error) {
        console.error('Error fetching payment config:', error);
      }
    };

    fetchPaymentConfig();
  }, []);

  // Calculate total amount from order data
  const totalAmount = useMemo(() => {
    if (!order?.priceAtOrder) return 0;

    const pricing = order.priceAtOrder;

    if (pricing.finalAmount !== undefined && pricing.finalAmount !== null) {
      return pricing.finalAmount;
    }

    const quantity = pricing.quantity || 1;
    let unitPrice = pricing.unitPrice || pricing.amount || 0;

    // Use size price if present (priceDelta is the actual price for that size)
    if (pricing.size?.priceDelta) {
      unitPrice = pricing.size.priceDelta;
    }

    let total = unitPrice * quantity;


    if (!pricing.deliveryIncluded && pricing.deliveryPrice) {
      total += pricing.deliveryPrice;
    }

    return Math.max(0, total);
  }, [order]);

  // Check if template can be edited (only before ACCEPTED status)
  const canEditTemplate = useMemo(() => {
    return order?.status === 'SUBMITTED';
  }, [order?.status]);

  // Handle start editing template
  const handleStartEdit = () => {
    if (order.template) {
      // Find the template index
      const templateIndex = product?.templates?.items?.findIndex(
        (t) => t.name === order.template.name
      );
      setSelectedTemplateIndex(templateIndex !== -1 ? templateIndex : null);
      setUploadedImages(order.template.images || []);
      setCustomText(order.template.customerNote || '');
    }
    setIsEditingTemplate(true);
  };

  // Handle cancel editing
  const handleCancelEdit = () => {
    setIsEditingTemplate(false);
    setSelectedTemplateIndex(null);
    setUploadedImages(order.template?.images || []);
    setCustomText(order.template?.customerNote || '');
  };

  // Handle template selection save
  const handleSaveTemplate = async () => {
    // If templates enabled, require selection. Else ignore.
    if (product?.templates?.enabled && product.templates.items.length > 0 && selectedTemplateIndex === null) {
      alert('⚠️ Please select a template');
      return;
    }

    setSavingTemplate(true);
    try {
      const selectedTemplate = (product?.templates?.enabled && selectedTemplateIndex !== null)
        ? product.templates.items[selectedTemplateIndex]
        : { name: 'Custom Design' };

      await updateDoc(doc(db, 'orders', orderId), {
        template: {
          name: selectedTemplate.name,
          quantity: order.priceAtOrder?.quantity || 1,
          images: uploadedImages,
          customerNote: customText.trim() || null,
          selectedAt: serverTimestamp(),
        },
        updatedAt: serverTimestamp(),
      });

      setIsEditingTemplate(false);
      alert('✅ Template saved successfully!');
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Failed to save template. Please try again.');
    } finally {
      setSavingTemplate(false);
    }
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (selectedTemplateIndex === null && !isEditingTemplate) {
      alert('Please select a template first');
      e.target.value = '';
      return;
    }

    setUploadingImages(true);
    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        if (file.size > 5 * 1024 * 1024) {
          throw new Error(`${file.name} is too large (max 5MB)`);
        }

        const timestamp = Date.now();
        const random = Math.random().toString(36).substring(7);
        const path = `orders/${orderId}/customer-photos/${timestamp}_${random}_${file.name}`;
        const storageReference = ref(storage, path);

        await uploadBytes(storageReference, file);
        const url = await getDownloadURL(storageReference);

        return {
          storagePath: path,
          url: url,
          fileName: file.name,
          uploadedAt: new Date(),
        };
      });

      const newImages = await Promise.all(uploadPromises);
      const updatedImages = [...uploadedImages, ...newImages];
      setUploadedImages(updatedImages);

      // Auto-save to Firestore to show in Admin immediately
      if (selectedTemplateIndex !== null && product?.templates?.items) {
        const selectedTemplate = product.templates.items[selectedTemplateIndex];

        const templateData = {
          name: selectedTemplate.name,
          quantity: order.priceAtOrder?.quantity || 1,
          images: updatedImages,
          customerNote: customText.trim() || null,
          selectedAt: order.template?.selectedAt || serverTimestamp(),
        };

        await updateDoc(doc(db, 'orders', orderId), {
          template: templateData,
          updatedAt: serverTimestamp()
        });
      }

      alert(`✅ ${newImages.length} photo(s) uploaded successfully!`);
      e.target.value = '';
    } catch (error) {
      console.error('Upload error:', error);
      alert(`Failed to upload: ${error.message}`);
      e.target.value = '';
    } finally {
      setUploadingImages(false);
    }
  };

  // Handle remove image
  const handleRemoveImage = async (index) => {
    if (!window.confirm('Remove this photo?')) return;

    const imageToRemove = uploadedImages[index];
    const updatedImages = uploadedImages.filter((_, i) => i !== index);
    setUploadedImages(updatedImages);

    // Delete from storage
    try {
      if (imageToRemove.storagePath) {
        const imageRef = ref(storage, imageToRemove.storagePath);
        await deleteObject(imageRef);
      }
    } catch (error) {
      console.error('Error deleting image from storage:', error);
    }

    // Auto-save to Firestore
    if (selectedTemplateIndex !== null && product?.templates?.items) {
      try {
        const selectedTemplate = product.templates.items[selectedTemplateIndex];

        const templateData = {
          name: selectedTemplate.name,
          quantity: order.priceAtOrder?.quantity || 1,
          images: updatedImages,
          customerNote: customText.trim() || null,
          selectedAt: order.template?.selectedAt || serverTimestamp(),
        };

        await updateDoc(doc(db, 'orders', orderId), {
          template: templateData,
          updatedAt: serverTimestamp()
        });
      } catch (err) {
        console.error('Error updating order after image removal:', err);
      }
    }
  };

  // Handle payment slip upload
  const handlePaymentSlipUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('File too large. Maximum size is 5MB.');
      e.target.value = '';
      return;
    }

    setUploadingPayment(true);
    try {
      const timestamp = Date.now();
      const path = `orders/${orderId}/payment-slips/${timestamp}_${file.name}`;
      const storageReference = ref(storage, path);

      await uploadBytes(storageReference, file);
      const url = await getDownloadURL(storageReference);

      await updateDoc(doc(db, 'orders', orderId), {
        payment: {
          proofUrl: url,
          storagePath: path,
          submittedAt: serverTimestamp(),
          status: 'PENDING_VERIFICATION',
        },
        updatedAt: serverTimestamp(),
      });

      alert('✅ Payment proof uploaded successfully! We will verify it shortly.');
      e.target.value = '';
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload payment proof. Please try again.');
      e.target.value = '';
    } finally {
      setUploadingPayment(false);
    }
  };

  // Render expired view
  if (expired) {
    return (
      <>
        <Helmet>
          <title>Order Link Expired - Lithophane Builder</title>
        </Helmet>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="max-w-md bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="text-6xl mb-4">🔒</div>
            <h1 className="text-2xl font-bold mb-4 text-gray-900">Link Expired</h1>
            <p className="text-gray-600 mb-6">
              This tracking link is no longer active. Your order has been completed and delivered.
            </p>
            <div className="space-y-3">
              <a
                href="https://wa.me/94771236474"
                target="_blank"
                rel="noopener noreferrer"
                className="block px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Contact us on WhatsApp
              </a>
              <a
                href="mailto:info@merchm.store"
                className="block px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Email Us
              </a>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your order...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-gray-600">Order not found</p>
        </div>
      </div>
    );
  }

  // Payment Method Selection Handler
  const handlePaymentMethodSelect = async (method) => {
    setSelectedPaymentMethod(method);
    try {
      await updateDoc(doc(db, 'orders', orderId), {
        'payment.method': method,
        'payment.updatedAt': serverTimestamp()
      });
    } catch (error) {
      console.error('Error saving payment method:', error);
    }
  };

  // Confirm COD Order
  const handleConfirmCOD = async () => {
    if (!window.confirm('Are you sure you want to confirm this order as Cash on Delivery?')) return;

    setUploadingPayment(true);
    try {
      await updateDoc(doc(db, 'orders', orderId), {
        'payment.status': 'PENDING_VERIFICATION',
        'payment.method': 'cod',
        'payment.submittedAt': serverTimestamp(),
        'payment.proofUrl': null // Ensure no proof is set (or maybe 'cod_confirmed'?)
      });
    } catch (error) {
      console.error('Error confirming COD:', error);
      alert('Failed to confirm order. Please try again.');
    } finally {
      setUploadingPayment(false);
    }
  };

  // Handle Total Amount Update (Admin Only)
  const handleUpdateTotalAmount = async () => {
    const amount = parseFloat(editedAmount);
    if (isNaN(amount) || amount < 0) {
      alert('Please enter a valid amount');
      return;
    }

    setUpdatingAmount(true);
    try {
      await updateDoc(doc(db, 'orders', orderId), {
        'priceAtOrder.finalAmount': amount,
        updatedAt: serverTimestamp(),
      });
      setIsEditingAmount(false);
    } catch (error) {
      console.error('Error updating total amount:', error);
      alert('Failed to update amount');
    } finally {
      setUpdatingAmount(false);
    }
  };

  // Timeline event configuration
  const timelineEvents = [];

  // 1. Order Placed
  timelineEvents.push({
    id: 'order-placed',
    title: 'Order Placed',
    icon: '📝',
    timestamp: order.createdAt,
    status: 'completed',
    content: (
      <div className="space-y-2 text-sm">
        <div><span className="font-medium">Order ID:</span> #{orderId.substring(0, 8).toUpperCase()}</div>
        <div><span className="font-medium">Product:</span> {order.productName}</div>
        {order.priceAtOrder?.size?.label && (
          <div>
            <span className="font-medium">Size/Variant:</span> {order.priceAtOrder.size.label}
            <span className="ml-2 text-xs text-gray-600">
              (LKR {order.priceAtOrder.size.priceDelta?.toLocaleString() || 'N/A'})
            </span>
          </div>
        )}
        {order.priceAtOrder?.quantity && order.priceAtOrder.quantity > 1 && (
          <div><span className="font-medium">Quantity:</span> {order.priceAtOrder.quantity}</div>
        )}
        <div><span className="font-medium">Customer:</span> {order.fulfillment?.recipient?.name}</div>
        <div><span className="font-medium">Delivery Date:</span> {order.fulfillment?.requestedDeliveryDate}</div>
        <div><span className="font-medium">Amount:</span> {order.priceAtOrder?.currency || 'Rs.'} {totalAmount.toLocaleString()}</div>
      </div>
    ),
  });

  // 2. Template Selection
  const hasImages = uploadedImages.length > 0;
  const showTemplateSection = !order.template || isEditingTemplate || canEditTemplate || hasImages;

  if (order.template || showTemplateSection) {
    timelineEvents.push({
      id: 'template-selection',
      title: order.template ? 'Template Selected' : 'Select Template',
      icon: '🎨',
      timestamp: order.template?.selectedAt,
      status: order.template ? 'completed' : 'pending',
      content: !order.template || isEditingTemplate ? (
        // Template Selection Form
        <div>
          {product?.templates?.enabled && product?.templates?.items?.length > 0 ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">Choose a template design for your {order.productName}</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {product.templates.items.map((template, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setSelectedTemplateIndex(index)}
                    className={`p-4 rounded-lg border-2 transition-all text-left ${selectedTemplateIndex === index
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-300 bg-white hover:border-purple-300'
                      }`}
                  >
                    <div className="font-semibold text-gray-900">{template.name}</div>
                    <div className="text-xs text-gray-600 mt-1">
                      {template.description || 'Select this template'}
                    </div>
                  </button>
                ))}
              </div>

            </div>
          ) : null}

          {/* Photo Upload Section - Always show if editing/no template OR if we have images */}
          {((product?.templates?.enabled && selectedTemplateIndex !== null) || (!product?.templates?.enabled) || hasImages) && (
            <div className="space-y-4 pt-4">
              {(!product?.templates?.enabled) && (
                <p className="text-sm text-gray-600">
                  {hasImages ? "Your design photos:" : "Upload your photos for this order."}
                </p>
              )}

              {/* Show upload controls only if editing */}
              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-800 mb-2">
                  {hasImages ? "Add More Photos" : "Upload Your Photos"}
                </label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  disabled={uploadingImages}
                  className="w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-purple-500 file:text-white hover:file:bg-purple-600 disabled:opacity-50"
                />
                {uploadingImages && <p className="text-sm text-purple-600 mt-2">Uploading...</p>}
              </div>

              {/* Uploaded Images */}
              {uploadedImages.length > 0 && (
                <div>
                  <p className="text-sm text-green-600 mb-2">
                    ✓ {uploadedImages.length} photo(s) uploaded
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {uploadedImages.map((img, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={img.url}
                          alt={`Upload ${index + 1}`}
                          onClick={() => setLightboxImage(img.url)}
                          className="w-full h-24 object-cover rounded border cursor-pointer hover:opacity-75"
                        />
                        {(isEditingTemplate || !order.template) && (
                          <button
                            type="button"
                            onClick={() => handleRemoveImage(index)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Custom Text */}
              <div>
                <label className="block text-sm font-medium text-gray-800 mb-2">
                  Special Instructions (Optional)
                </label>
                <textarea
                  value={customText}
                  onChange={(e) => setCustomText(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Any special instructions, names to print, or other details..."
                />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleSaveTemplate}
                  disabled={savingTemplate}
                  className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-semibold"
                >
                  {savingTemplate ? 'Saving...' : 'Save Changes'}
                </button>
                {isEditingTemplate && (
                  <button
                    onClick={handleCancelEdit}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        // Template Details (Read-only)
        <div className="space-y-3">
          <div>
            <p className="text-sm text-gray-600">Selected Template</p>
            <p className="font-medium text-gray-900">{order.template.name}</p>
          </div>
          {order.template.images && order.template.images.length > 0 && (
            <div>
              <p className="text-sm text-gray-600 mb-2">Your Photos ({order.template.images.length})</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {order.template.images.map((img, index) => (
                  <img
                    key={index}
                    src={img.url}
                    alt={`Photo ${index + 1}`}
                    onClick={() => setLightboxImage(img.url)}
                    className="w-full h-24 object-cover rounded border cursor-pointer hover:opacity-75"
                  />
                ))}
              </div>
            </div>
          )}
          {order.template.customerNote && (
            <div>
              <p className="text-sm text-gray-600">Special Instructions</p>
              <p className="text-gray-900 whitespace-pre-wrap">{order.template.customerNote}</p>
            </div>
          )}
          {canEditTemplate && !isEditingTemplate && (
            <button
              onClick={handleStartEdit}
              className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
            >
              Edit Template & Photos
            </button>
          )}
        </div>
      ),
    });
  }

  // 3. Payment Submission
  const isPaymentPendingVerification = order.payment?.status === 'PENDING_VERIFICATION';
  const showPaymentSubmitted = order.payment?.proofUrl || (order.payment?.method === 'cod' && isPaymentPendingVerification);

  timelineEvents.push({
    id: 'payment',
    title: showPaymentSubmitted ? 'Payment Submitted' : 'Submit Payment',
    icon: '💳',
    timestamp: order.payment?.submittedAt,
    status: showPaymentSubmitted ? 'completed' : 'pending',
    content: (
      <div className="space-y-4">
        {/* Amount */}
        <div className="bg-purple-50 p-4 rounded-lg">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm text-gray-600">Total Amount</p>
            {userRole === 'admin' && !isEditingAmount && (
              <button
                onClick={() => {
                  setEditedAmount(totalAmount.toString());
                  setIsEditingAmount(true);
                }}
                className="p-1 hover:bg-purple-100 rounded text-purple-600"
                title="Edit Amount (Admin Only)"
              >
                <Edit className="w-4 h-4" />
              </button>
            )}
          </div>

          {isEditingAmount ? (
            <div className="flex items-center gap-2">
              <span className="text-gray-500 font-bold">{order.priceAtOrder?.currency || 'Rs.'}</span>
              <input
                type="number"
                value={editedAmount}
                onChange={(e) => setEditedAmount(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleUpdateTotalAmount()}
                className="w-32 px-2 py-1 border border-purple-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500 font-bold text-lg"
                autoFocus
              />
              <button
                onClick={handleUpdateTotalAmount}
                disabled={updatingAmount}
                className="p-1 bg-green-100 text-green-600 rounded hover:bg-green-200"
              >
                <Check className="w-5 h-5" />
              </button>
              <button
                onClick={() => setIsEditingAmount(false)}
                className="p-1 bg-red-100 text-red-600 rounded hover:bg-red-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <div className="flex items-baseline gap-2">
              <p className="text-2xl font-bold text-purple-600">
                {order.priceAtOrder?.currency || 'Rs.'} {selectedPaymentMethod === 'kokoPay' ? Math.round(totalAmount * 1.13).toLocaleString() : totalAmount.toLocaleString()}
              </p>
              {selectedPaymentMethod === 'kokoPay' && (
                <span className="text-xs text-amber-600 font-medium">(with 13% KOKO fee)</span>
              )}
            </div>
          )}

        </div>

        {/* Payment Method Selector */}
        {!showPaymentSubmitted && product?.paymentOptions?.paymentMethods && (
          <div className="flex flex-wrap gap-2 mb-2">
            {product.paymentOptions.paymentMethods.onlineTransfer && (
              <button
                onClick={() => handlePaymentMethodSelect('onlineTransfer')}
                className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${selectedPaymentMethod === 'onlineTransfer' ? 'bg-purple-100 border-purple-500 text-purple-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
              >
                🏦 Bank Transfer
              </button>
            )}
            {product.paymentOptions.paymentMethods.creditDebit && (
              <button
                onClick={() => handlePaymentMethodSelect('creditDebit')}
                className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${selectedPaymentMethod === 'creditDebit' ? 'bg-purple-100 border-purple-500 text-purple-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
              >
                💳 Card
              </button>
            )}
            {product.paymentOptions.paymentMethods.kokoPay && (
              <button
                onClick={() => handlePaymentMethodSelect('kokoPay')}
                className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${selectedPaymentMethod === 'kokoPay' ? 'bg-purple-100 border-purple-500 text-purple-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
              >
                🔄 KOKO
              </button>
            )}
            {product.paymentOptions.paymentMethods.cod && (
              <button
                onClick={() => handlePaymentMethodSelect('cod')}
                className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${selectedPaymentMethod === 'cod' ? 'bg-purple-100 border-purple-500 text-purple-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
              >
                📦 COD
              </button>
            )}
          </div>
        )}

        {/* Method Specific Content */}
        {!showPaymentSubmitted && (
          <>
            {/* Bank Transfer Details */}
            {selectedPaymentMethod === 'onlineTransfer' && paymentConfig?.bankDetails && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-900 mb-3">Bank Transfer Details</p>
                <div className="space-y-2 text-sm">
                  <div><span className="text-gray-600">Bank:</span> <span className="font-medium">{paymentConfig.bankDetails.bankName}</span></div>
                  <div><span className="text-gray-600">Account Name:</span> <span className="font-medium">{paymentConfig.bankDetails.accountName}</span></div>
                  <div><span className="text-gray-600">Account Number:</span> <span className="font-medium">{paymentConfig.bankDetails.accountNumber}</span></div>
                  <div><span className="text-gray-600">Branch:</span> <span className="font-medium">{paymentConfig.bankDetails.branch}</span></div>
                </div>
              </div>
            )}

            {/* Credit/Debit Content */}
            {selectedPaymentMethod === 'creditDebit' && (
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <p className="font-semibold text-gray-900 mb-2">Pay Securely Online</p>
                <p className="text-sm text-gray-600 mb-4">Click the button below to pay via PayHere.</p>
                {product?.paymentOptions?.paymentLink ? (
                  <a
                    href={product.paymentOptions.discount?.enabled && product.paymentOptions.discount?.paymentLink ? product.paymentOptions.discount.paymentLink : product.paymentOptions.paymentLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold transition-colors"
                  >
                    Pay {order.priceAtOrder?.currency} {product.paymentOptions.discount?.enabled ? ((product.paymentOptions.amount || totalAmount) - product.paymentOptions.discount.amount).toLocaleString() : totalAmount.toLocaleString()} Now
                  </a>
                ) : (
                  <div className="text-sm text-amber-600 bg-amber-50 p-3 rounded">
                    Payment link will be sent by admin shortly.
                  </div>
                )}
              </div>
            )}

            {/* Koko Pay Content */}
            {selectedPaymentMethod === 'kokoPay' && (
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <p className="font-semibold text-amber-900 mb-2">KOKO Pay (3 Installments)</p>
                <p className="text-sm text-amber-800">
                  Please note that a 13% service fee will be added. Total to pay: <b>{order.priceAtOrder?.currency || 'Rs.'} {Math.round(totalAmount * 1.13).toLocaleString()}</b>
                </p>
                <div className="mt-3 p-3 bg-white rounded border border-amber-100">
                  <p className="text-sm text-gray-600">Admin will send you the KOKO Pay link shortly via WhatsApp.</p>
                </div>
              </div>
            )}

            {/* COD Content */}
            {selectedPaymentMethod === 'cod' && (
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <p className="font-semibold text-green-900 mb-2">Cash on Delivery</p>
                <p className="text-sm text-green-800 mb-3">
                  You can pay in cash when you receive the package.
                </p>
                <button
                  onClick={handleConfirmCOD}
                  disabled={uploadingPayment}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold disabled:opacity-50"
                >
                  {uploadingPayment ? 'Confirming...' : 'Confirm COD Order'}
                </button>
              </div>
            )}
          </>
        )}

        {/* Payment Status & Upload */}
        {showPaymentSubmitted ? (
          <div>
            <div className="flex items-center gap-2 mb-2">
              {(() => {
                const isVerified =
                  order.payment.status === 'VERIFIED' ||
                  order.payment.verified === true ||
                  (order.status !== 'SUBMITTED' && order.payment.proofUrl);
                const isRejected = order.payment.status === 'REJECTED';
                const isCodPending = order.payment?.method === 'cod' && order.payment?.status === 'PENDING_VERIFICATION';

                if (isCodPending) {
                  return (
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                      ⏳ Pending Admin Approval (COD)
                    </span>
                  )
                }

                return (
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${isVerified
                      ? 'bg-green-100 text-green-800'
                      : isRejected
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                      }`}
                  >
                    {isVerified
                      ? '✓ Verified'
                      : isRejected
                        ? '✗ Rejected'
                        : '⏳ Pending Verification'}
                  </span>
                );
              })()}
            </div>
            {order.payment?.proofUrl ? (
              <>
                <p className="text-sm text-gray-600 mb-2">Uploaded Payment Slip</p>
                <img
                  src={order.payment.proofUrl}
                  alt="Payment slip"
                  onClick={() => setLightboxImage(order.payment.proofUrl)}
                  className="max-w-sm rounded border cursor-pointer hover:opacity-75"
                />
              </>
            ) : order.payment?.method === 'cod' ? (
              <p className="text-sm text-green-700">
                Order confirmed as COD. Helper will verify shortly.
              </p>
            ) : null}
          </div>
        ) : (
          /* Show Upload UI for Online Transfer or Default IF NOT COD */
          /* selectedPaymentMethod is not COD since showPaymentSubmitted is false, and COD shows confirm button instead */
          (selectedPaymentMethod === 'onlineTransfer' || !product?.paymentOptions?.paymentMethods) && (
            <div>
              <label className="block text-sm font-medium text-gray-800 mb-2">
                Upload Payment Slip
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handlePaymentSlipUpload}
                disabled={uploadingPayment}
                className="w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-green-500 file:text-white hover:file:bg-green-600 disabled:opacity-50"
              />
              {uploadingPayment && <p className="text-sm text-green-600 mt-2">Uploading...</p>}
            </div>
          )
        )}
      </div>
    ),
  });

  // 4. Production Started (Design Approved)
  if (order.designPhotos && order.designPhotos.length > 0) {
    timelineEvents.push({
      id: 'production-started',
      title: 'Production Started',
      icon: '🎨',
      timestamp: order.statusHistory?.IN_PRODUCTION?.completedAt || order.statusHistory?.ACCEPTED?.completedAt,
      status: order.status === 'IN_PRODUCTION' || order.status === 'COMPLETED' || order.status === 'DISPATCHED' || order.status === 'DELIVERED' ? 'completed' : 'pending',
      content: (
        <div>
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
            <p className="text-sm text-blue-800 font-medium">
              ✓ This design has been approved by you and production has started.
            </p>
            <p className="text-xs text-blue-600 mt-1">
              Note: Design cannot be changed at this stage.
            </p>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Here are the approved design images for your order:
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {order.designPhotos.map((photo, index) => (
              <img
                key={index}
                src={photo}
                alt={`Design ${index + 1}`}
                onClick={() => setLightboxImage(photo)}
                className="w-full h-32 object-cover rounded border cursor-pointer hover:opacity-75"
              />
            ))}
          </div>
        </div>
      ),
    });
  }

  // 5. Production Completed Photos
  if (order.progress?.productionPhotos && order.progress.productionPhotos.length > 0) {
    timelineEvents.push({
      id: 'production-completed',
      title: 'Production Completed',
      icon: '✨',
      timestamp: order.statusHistory?.COMPLETED?.completedAt,
      status: 'completed',
      content: (
        <div>
          <p className="text-sm text-gray-600 mb-4">
            Your order has been completed! Here are photos of the final product.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {order.progress.productionPhotos.map((photo, index) => (
              <img
                key={index}
                src={photo.url || photo}
                alt={`Production ${index + 1}`}
                onClick={() => setLightboxImage(photo.url || photo)}
                className="w-full h-32 object-cover rounded border cursor-pointer hover:opacity-75"
              />
            ))}
          </div>
        </div>
      ),
    });
  }

  // 6. Delivery
  if (order.progress?.deliveryPhotoUrl) {
    timelineEvents.push({
      id: 'delivery',
      title: 'Dispatched',
      icon: '🚚',
      timestamp: order.statusHistory?.DISPATCHED?.completedAt,
      status: 'completed',
      content: (
        <div>
          <p className="text-sm text-gray-600 mb-4">
            Your order has been dispatched! Here's your delivery note.
          </p>
          <img
            src={order.progress.deliveryPhotoUrl}
            alt="Delivery note"
            onClick={() => setLightboxImage(order.progress.deliveryPhotoUrl)}
            className="max-w-md rounded border cursor-pointer hover:opacity-75"
          />
          <a
            href={order.progress.deliveryPhotoUrl}
            download
            className="mt-3 inline-block px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
          >
            Download Delivery Note
          </a>
        </div>
      ),
    });
  }

  return (
    <>
      <Helmet>
        <title>Track Order #{orderId.substring(0, 8).toUpperCase()} - Lithophane Builder</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Order #{orderId.substring(0, 8).toUpperCase()}
            </h1>
            <div className="flex items-center gap-2">
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${order.status === 'SUBMITTED'
                  ? 'bg-blue-100 text-blue-800'
                  : order.status === 'ACCEPTED'
                    ? 'bg-green-100 text-green-800'
                    : order.status === 'IN_PRODUCTION'
                      ? 'bg-purple-100 text-purple-800'
                      : order.status === 'COMPLETED'
                        ? 'bg-teal-100 text-teal-800'
                        : order.status === 'DISPATCHED'
                          ? 'bg-orange-100 text-orange-800'
                          : 'bg-gray-100 text-gray-800'
                  }`}
              >
                {order.status.replace('_', ' ')}
              </span>
            </div>
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* Vertical Line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>

            {/* Timeline Events - Reversed to show newest at top */}
            <div className="space-y-6">
              {[...timelineEvents].reverse().map((event, index) => (
                <div key={event.id} className="relative flex gap-4">
                  {/* Icon Circle */}
                  <div
                    className={`relative z-10 flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-xl ${event.status === 'completed'
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-300 text-gray-600'
                      }`}
                  >
                    {event.icon}
                  </div>

                  {/* Content Card */}
                  <div className="flex-1 bg-white rounded-lg shadow-sm p-6 mb-4">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{event.title}</h3>
                        {event.timestamp && (
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(event.timestamp.toDate ? event.timestamp.toDate() : event.timestamp).toLocaleString()}
                          </p>
                        )}
                      </div>
                    </div>
                    <div>{event.content}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button
            className="absolute top-4 right-4 text-white text-4xl hover:text-gray-300"
            onClick={() => setLightboxImage(null)}
          >
            ×
          </button>
          <img
            src={lightboxImage}
            alt="Preview"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
